import { Component, computed, EventEmitter, Input, input, OnInit, Output, signal, ViewChild } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { GalaxyColumnDef, GalaxyDataSource, GalaxyTableModule, MODULE_IMPORTS } from '@vendasta/galaxy/table';
import {
  GalaxyFilterChipInjectionToken,
  GalaxyFilterInterface,
  GalaxyFilterOperator,
  GalaxyFilterType,
} from '@vendasta/galaxy/filter/chips';
import { Dispute, DisputeStatus, RetailPaymentReferenceTypeEnum } from '@galaxy/billing';
import { DisputesTableDatasource } from './disputes-table.datasource';
import { GalaxyFilterChipsComponent } from '@vendasta/galaxy/filter/chips/src/galaxy-filter-chips.component';
import { DisputeReasonDisplayPipe, PadInvoiceNumberPipe, PricePipe } from '../../shared/pipes';
import { PaymentStatusChipComponent } from '../../payment-status-chip/payment-status-chip.component';
import { DateFormat } from '@vendasta/galaxy/utility/date-utils';

export interface DisputeTableOptions {
  forcedFilters?: ForcedFilterOptions;
}

export interface ForcedFilterOptions {
  status: DisputeStatus;
}

@Component({
  selector: 'smb-invoicing-disputes-table',
  templateUrl: './disputes-table.component.html',
  styleUrls: ['./disputes-table.component.scss'],
  standalone: true,
  imports: [
    GalaxyPageModule,
    TranslateModule,
    GalaxyTableModule,
    GalaxyFilterChipsComponent,
    MODULE_IMPORTS,
    DisputeReasonDisplayPipe,
    PaymentStatusChipComponent,
    PricePipe,
    PadInvoiceNumberPipe,
  ],
  providers: [
    DisputesTableDatasource,
    { provide: GalaxyFilterChipInjectionToken, useExisting: DisputesTableDatasource },
  ],
})
export class DisputesTableComponent implements OnInit {
  DateFormat = DateFormat;
  PaymentReferenceType = RetailPaymentReferenceTypeEnum;

  isForcedFilteredView = signal<boolean>(false);
  noFiltersApplied = computed(() => this.disputeTableDataSource.noFiltersApplied());
  merchantId = input<string>();
  canClickPaymentReference = input<boolean>(true);
  customerTitleTranslationKey = input<string>('DISPUTES.TABLE.COLUMN_HEADERS.CUSTOMER');
  @ViewChild(GalaxyFilterChipsComponent) filtersComponent?: GalaxyFilterChipsComponent;
  @Input() set tableOptions(tableOptions: DisputeTableOptions) {
    if (tableOptions?.forcedFilters?.status) {
      this.isForcedFilteredView.set(true);
      this.dataSource.setFilters([]); // ensures glxy table is reset (back to 1st page; loading state)
      this.setTableFiltersForStatus(tableOptions?.forcedFilters?.status);
    } else {
      this.isForcedFilteredView.set(false);
      this.dataSource.setFilters([]); // ensures glxy table is reset (back to 1st page; loading state)
      this.filtersComponent?.clearFilters(); // ensures glxy filter chips are removed
      this.disputeTableDataSource.setInitialAppliedFilters([]);
    }
  }

  @Output() disputeClicked = new EventEmitter<string>();
  @Output() paymentReferenceClicked = new EventEmitter<Dispute>();
  @Output() customerClicked = new EventEmitter<string>();

  columns: GalaxyColumnDef[];

  public dataSource: GalaxyDataSource<Dispute>;

  constructor(
    private disputeTableDataSource: DisputesTableDatasource,
    private translateService: TranslateService,
  ) {
    this.dataSource = new GalaxyDataSource<Dispute>(this.disputeTableDataSource);
  }

  ngOnInit() {
    this.columns = [
      {
        id: 'description',
        title: this.translateService.instant('DISPUTES.TABLE.COLUMN_HEADERS.DESCRIPTION'),
      },
      {
        id: 'customerId',
        title: this.translateService.instant(this.customerTitleTranslationKey()),
      },
      {
        id: 'created',
        title: this.translateService.instant('DISPUTES.TABLE.COLUMN_HEADERS.CREATED'),
      },
      {
        id: 'due',
        title: this.translateService.instant('DISPUTES.TABLE.COLUMN_HEADERS.RESPONSE_DUE'),
      },
      {
        id: 'status',
        title: this.translateService.instant('DISPUTES.TABLE.COLUMN_HEADERS.STATUS'),
      },
      {
        id: 'resolvedAt',
        title: this.translateService.instant('DISPUTES.TABLE.COLUMN_HEADERS.RESOLVED'),
      },
      {
        id: 'reason',
        title: this.translateService.instant('DISPUTES.TABLE.COLUMN_HEADERS.REASON'),
      },
      {
        id: 'amount',
        title: this.translateService.instant('DISPUTES.TABLE.COLUMN_HEADERS.AMOUNT'),
      },
    ];

    this.disputeTableDataSource.setMerchantId(this.merchantId());
  }

  onFilterChanged(filters: GalaxyFilterInterface[]): void {
    this.disputeTableDataSource.updateFilters(filters);
  }

  onRowClicked(dispute: Dispute): void {
    this.disputeClicked.emit(dispute.paymentId);
  }

  public paymentReferenceClickEvent(event: Event, dispute: Dispute): void {
    this.paymentReferenceClicked.emit(dispute);
    this.consumeEvent(event);
  }

  public customerClickEvent(event: Event, customerId: string): void {
    this.customerClicked.emit(customerId);
    this.consumeEvent(event);
  }

  public consumeEvent(event: Event): void {
    event.stopPropagation();
  }

  private setTableFiltersForStatus(status: DisputeStatus): void {
    const filters = [
      {
        fieldId: 'status',
        operator: GalaxyFilterOperator.FILTER_OPERATOR_IS,
        values: [{ string: status }],
        definition: {
          fieldId: 'status',
          type: GalaxyFilterType.FILTER_TYPE_CHECKBOX,
          supportedOperators: [GalaxyFilterOperator.FILTER_OPERATOR_IS],
          fieldName: this.translateService.instant('DISPUTES.TABLE.COLUMN_HEADERS.STATUS'),
        },
      },
    ];
    this.disputeTableDataSource.setInitialAppliedFilters(filters);
  }
}
